import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AppConfigType, AppConfigView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateAdminAppConfigs } from "@/lib/query/core/invalidation";

type CreateAppConfigInput = {
  key: string;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  type: AppConfigType;
  scope: "runtime" | "boot";
  value: string;
  minInt?: number;
  maxInt?: number;
};

// Creates a free-form custom key. Registry keys can't be added through
// here — backend rejects with 409 Conflict. The mutation surfaces the
// raw error so the dialog can show backend validation messages
// (regex mismatch, key collision, etc.).
export function useCreateAppConfigMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: CreateAppConfigInput) =>
      apiJsonClient<AppConfigView>(endpoints.admin.appConfigs, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: async () => {
      await invalidateAdminAppConfigs(queryClient);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
